import { ADVANCED_AUDIO_CONFIG } from './config/VoiceToText.config';

export interface NoiseGateState {
  isOpen: boolean;
  holdEndTime: number;
  lastLevel: number;
}

export interface AudioAnalysis {
  totalSamples: number;
  averageLevel: number;
  peakLevel: number;
  averageSpeechLevel: number;
  averageMusicLevel: number;
  speechToMusicRatio: number;
  activeRatio: number;
  isSilence: boolean;
  isMusicOnly: boolean;
  hasSpeech: boolean;
  speechConsistency: number;
  musicConsistency: number;
}

export class AudioProcessor {
  private audioContext: AudioContext | null = null;

  private analyser: AnalyserNode | null = null;

  private sourceNode: MediaStreamAudioSourceNode | null = null;

  private highPassFilter: BiquadFilterNode | null = null;

  private lowPassFilter: BiquadFilterNode | null = null;

  private compressor: DynamicsCompressorNode | null = null;

  private gainNode: GainNode | null = null;

  private noiseGate: NoiseGateState = {
    isOpen: false,
    holdEndTime: 0,
    lastLevel: 0,
  };

  private audioLevels: number[] = [];

  private speechLevels: number[] = [];

  private musicLevels: number[] = [];

  private silenceLevels: number[] = [];

  private monitoringId: number | null = null;

  constructor() {
    this.reset();
  }

  reset() {
    this.cleanup();
    this.noiseGate = { isOpen: false, holdEndTime: 0, lastLevel: 0 };
    this.audioLevels = [];
    this.speechLevels = [];
    this.musicLevels = [];
    this.silenceLevels = [];
  }

  cleanup() {
    if (this.monitoringId) {
      cancelAnimationFrame(this.monitoringId);
      this.monitoringId = null;
    }

    // Disconnect nodes in reverse order
    const nodes = [
      this.gainNode,
      this.compressor,
      this.lowPassFilter,
      this.highPassFilter,
      this.analyser,
      this.sourceNode,
    ];

    nodes.forEach((node) => {
      if (node) {
        try {
          node.disconnect();
        } catch (err) {
          // Node might already be disconnected
        }
      }
    });

    if (this.audioContext?.state !== 'closed') {
      this.audioContext?.close();
    }

    this.audioContext = null;
    this.analyser = null;
    this.sourceNode = null;
    this.highPassFilter = null;
    this.lowPassFilter = null;
    this.compressor = null;
    this.gainNode = null;
  }

  private applyNoiseGate(level: number, currentTime: number): number {
    const config = ADVANCED_AUDIO_CONFIG.processing.noiseGate;

    if (level > config.threshold) {
      this.noiseGate.isOpen = true;
      this.noiseGate.holdEndTime = currentTime + config.holdTime;
      this.noiseGate.lastLevel = level;
      return level;
    } else if (
      this.noiseGate.isOpen &&
      currentTime < this.noiseGate.holdEndTime
    ) {
      return level * 0.8;
    } else if (this.noiseGate.isOpen) {
      this.noiseGate.isOpen = false;
      return level * 0.1;
    }

    return level * 0.05;
  }

  private createProcessingChain(source: MediaStreamAudioSourceNode): AudioNode {
    try {
      if (!this.audioContext) return source;

      const { voiceFreqRange, noiseGate: noiseGateConfig } =
        ADVANCED_AUDIO_CONFIG.processing;

      // High-pass filter to remove low-frequency noise
      this.highPassFilter = this.audioContext.createBiquadFilter();
      this.highPassFilter.type = 'highpass';
      this.highPassFilter.frequency.setValueAtTime(
        voiceFreqRange.min,
        this.audioContext.currentTime,
      );
      this.highPassFilter.Q.setValueAtTime(0.7, this.audioContext.currentTime);

      // Low-pass filter to remove high-frequency noise
      this.lowPassFilter = this.audioContext.createBiquadFilter();
      this.lowPassFilter.type = 'lowpass';
      this.lowPassFilter.frequency.setValueAtTime(
        voiceFreqRange.max,
        this.audioContext.currentTime,
      );
      this.lowPassFilter.Q.setValueAtTime(0.7, this.audioContext.currentTime);

      // Compressor for dynamic range control
      this.compressor = this.audioContext.createDynamicsCompressor();
      this.compressor.threshold.setValueAtTime(
        -24,
        this.audioContext.currentTime,
      );
      this.compressor.knee.setValueAtTime(30, this.audioContext.currentTime);
      this.compressor.ratio.setValueAtTime(
        noiseGateConfig.ratio,
        this.audioContext.currentTime,
      );
      this.compressor.attack.setValueAtTime(
        noiseGateConfig.attack,
        this.audioContext.currentTime,
      );
      this.compressor.release.setValueAtTime(
        noiseGateConfig.release,
        this.audioContext.currentTime,
      );

      // Gain node for volume control
      this.gainNode = this.audioContext.createGain();
      this.gainNode.gain.setValueAtTime(1.0, this.audioContext.currentTime);

      // Connect the processing chain
      source.connect(this.highPassFilter);
      this.highPassFilter.connect(this.lowPassFilter);
      this.lowPassFilter.connect(this.compressor);
      this.compressor.connect(this.gainNode);

      return this.gainNode;
    } catch (err) {
      return source;
    }
  }

  setupMonitoring(
    stream: MediaStream,
    onError?: (error: string) => void,
  ): boolean {
    try {
      this.audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      this.sourceNode = this.audioContext.createMediaStreamSource(stream);

      const processedOutput = this.createProcessingChain(this.sourceNode);

      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = ADVANCED_AUDIO_CONFIG.processing.fftSize;
      this.analyser.smoothingTimeConstant =
        ADVANCED_AUDIO_CONFIG.processing.smoothingTimeConstant;
      this.analyser.minDecibels = -100;
      this.analyser.maxDecibels = -10;

      processedOutput.connect(this.analyser);

      this.audioLevels = [];
      this.speechLevels = [];
      this.musicLevels = [];
      this.silenceLevels = [];
      this.startMonitoring();

      return true;
    } catch (err) {
      onError?.(`Audio monitoring setup failed: ${err}`);
      return false;
    }
  }

  private startMonitoring() {
    const monitorLevels = () => {
      if (!this.analyser || !this.audioContext) return;

      const bufferLength = this.analyser.frequencyBinCount;
      const timeDataArray = new Uint8Array(this.analyser.fftSize);
      const freqDataArray = new Uint8Array(bufferLength);

      this.analyser.getByteTimeDomainData(timeDataArray);
      this.analyser.getByteFrequencyData(freqDataArray);

      // Calculate RMS for overall audio level
      let rmsSum = 0;
      for (let i = 0; i < timeDataArray.length; i++) {
        const sample = (timeDataArray[i] - 128) / 128;
        rmsSum += sample * sample;
      }
      const rms = Math.sqrt(rmsSum / timeDataArray.length);

      // Enhanced frequency analysis for better speech/music separation
      const { sampleRate } = ADVANCED_AUDIO_CONFIG.processing;
      const nyquist = sampleRate / 2;

      // Define more precise frequency bands
      const speechCoreStart = Math.floor((300 / nyquist) * bufferLength); // Core speech 300-3400Hz
      const speechCoreEnd = Math.floor((3400 / nyquist) * bufferLength);
      const speechExtendedStart = Math.floor((200 / nyquist) * bufferLength); // Extended speech 200-4000Hz
      const speechExtendedEnd = Math.floor((4000 / nyquist) * bufferLength);

      // Music frequency bands
      const musicBassStart = Math.floor((20 / nyquist) * bufferLength); // Bass 20-200Hz
      const musicBassEnd = Math.floor((200 / nyquist) * bufferLength);
      const musicHighStart = Math.floor((4000 / nyquist) * bufferLength); // High freq 4000-8000Hz
      const musicHighEnd = Math.floor((8000 / nyquist) * bufferLength);

      let speechCoreEnergy = 0;
      let speechExtendedEnergy = 0;
      let musicBassEnergy = 0;
      let musicHighEnergy = 0;

      // Analyze frequency content with better precision
      for (let i = 0; i < bufferLength; i++) {
        const amplitude = freqDataArray[i] / 255.0;

        // Core speech frequencies (most important for human voice)
        if (i >= speechCoreStart && i <= speechCoreEnd) {
          speechCoreEnergy += amplitude * 4.0; // Heavy weight for core speech
        }

        // Extended speech frequencies
        if (i >= speechExtendedStart && i <= speechExtendedEnd) {
          speechExtendedEnergy += amplitude * 2.0;
        }

        // Music bass frequencies (typical of music but not speech)
        if (i >= musicBassStart && i <= musicBassEnd) {
          musicBassEnergy += amplitude * 1.5;
        }

        // Music high frequencies
        if (i >= musicHighStart && i <= musicHighEnd) {
          musicHighEnergy += amplitude * 1.2;
        }
      }

      // Normalize energies by frequency band size
      const speechLevel =
        (speechCoreEnergy + speechExtendedEnergy) /
        (speechCoreEnd -
          speechCoreStart +
          (speechExtendedEnd - speechExtendedStart));
      const musicLevel =
        (musicBassEnergy + musicHighEnergy) /
        (musicBassEnd - musicBassStart + (musicHighEnd - musicHighStart));
      const silenceLevel = Math.max(
        0,
        10 - (rms * 100 + speechLevel * 50 + musicLevel * 30),
      );

      // Apply noise gate to combined level
      const combinedLevel = rms * 20 + speechLevel * 80; // Prioritize speech heavily
      const gatedLevel = this.applyNoiseGate(
        combinedLevel,
        this.audioContext.currentTime,
      );

      // Dynamic gain adjustment for speech enhancement
      if (this.gainNode) {
        try {
          const dB = 20 * Math.log10(Math.max(gatedLevel, 0.001));
          if (dB < -60) {
            this.gainNode.gain.setTargetAtTime(
              1.8,
              this.audioContext.currentTime,
              0.1,
            );
          } else if (dB > -20) {
            this.gainNode.gain.setTargetAtTime(
              0.6,
              this.audioContext.currentTime,
              0.1,
            );
          } else {
            this.gainNode.gain.setTargetAtTime(
              1.0,
              this.audioContext.currentTime,
              0.1,
            );
          }
        } catch (err) {
          // Gain node might be disconnected
        }
      }

      // Store levels for analysis
      const volumeLevel = Math.min(gatedLevel * 2, 100);
      this.audioLevels.push(volumeLevel);
      this.speechLevels.push(speechLevel * 100);
      this.musicLevels.push(musicLevel * 100);
      this.silenceLevels.push(silenceLevel);

      // Limit array sizes to prevent memory issues
      if (this.audioLevels.length > 1000) {
        this.audioLevels = this.audioLevels.slice(-500);
        this.speechLevels = this.speechLevels.slice(-500);
        this.musicLevels = this.musicLevels.slice(-500);
        this.silenceLevels = this.silenceLevels.slice(-500);
      }

      // Continue monitoring
      this.monitoringId = requestAnimationFrame(monitorLevels);
    };

    // Start monitoring after a brief delay
    setTimeout(() => {
      monitorLevels();
    }, 100);
  }

  stopMonitoring() {
    if (this.monitoringId) {
      cancelAnimationFrame(this.monitoringId);
      this.monitoringId = null;
    }
  }

  hasSufficientAudio(): boolean {
    if (this.audioLevels.length < 20) {
      return false; // Need at least 2 seconds of audio for reliable analysis
    }

    const analysis = this.getAnalysisReport();

    // 1. Strict silence detection - if it's clearly silence, reject immediately
    if (analysis.isSilence) {
      return false;
    }

    // 2. Strict music-only detection
    if (analysis.isMusicOnly) {
      return false;
    }

    // 3. Speech level threshold - lowered for normal conversational speech
    if (analysis.averageSpeechLevel < 5.0) {
      return false;
    }

    // 4. Speech should be somewhat dominant over background
    if (analysis.speechToMusicRatio < 1.5) {
      return false;
    }

    // 5. Allow for natural speech variations and pauses
    if (analysis.speechConsistency < 0.4) {
      return false;
    }

    // 6. Natural speaking activity ratio - lowered to allow for pauses
    if (analysis.activeRatio < 0.5) {
      return false;
    }

    // 7. Peak level for normal conversational speech
    if (analysis.peakLevel < 20.0) {
      return false;
    }

    // 8. Final check: Must pass the hasSpeech detection
    if (!analysis.hasSpeech) {
      return false;
    }

    return true;
  }

  getAudioLevels(): number[] {
    return [...this.audioLevels];
  }

  getSpeechLevels(): number[] {
    return [...this.speechLevels];
  }

  getMusicLevels(): number[] {
    return [...this.musicLevels];
  }

  getCurrentLevel(): number {
    return this.audioLevels.length > 0
      ? this.audioLevels[this.audioLevels.length - 1]
      : 0;
  }

  getAnalysisReport(): AudioAnalysis {
    if (this.audioLevels.length === 0) {
      return {
        totalSamples: 0,
        averageLevel: 0,
        peakLevel: 0,
        averageSpeechLevel: 0,
        averageMusicLevel: 0,
        speechToMusicRatio: 0,
        activeRatio: 0,
        isSilence: true,
        isMusicOnly: false,
        hasSpeech: false,
        speechConsistency: 0,
        musicConsistency: 0,
      };
    }

    // Calculate basic metrics
    const averageLevel =
      this.audioLevels.reduce((sum, level) => sum + level, 0) /
      this.audioLevels.length;
    const peakLevel = Math.max(...this.audioLevels);
    const averageSpeechLevel =
      this.speechLevels.reduce((sum, level) => sum + level, 0) /
      this.speechLevels.length;
    const averageMusicLevel =
      this.musicLevels.reduce((sum, level) => sum + level, 0) /
      this.musicLevels.length;
    const averageSilenceLevel =
      this.silenceLevels.reduce((sum, level) => sum + level, 0) /
      this.silenceLevels.length;

    // Calculate activity ratio
    const activeSamples = this.audioLevels.filter(
      (level) => level > 1.0,
    ).length;
    const activeRatio = activeSamples / this.audioLevels.length;

    // Calculate speech to music ratio
    const speechToMusicRatio =
      averageMusicLevel > 0.1
        ? averageSpeechLevel / averageMusicLevel
        : averageSpeechLevel * 10; // If no music, boost speech ratio

    // Calculate consistency metrics
    const speechSamples = this.speechLevels.filter(
      (level) => level > 2.0,
    ).length;
    const musicSamples = this.musicLevels.filter((level) => level > 3.0).length;
    const speechConsistency = speechSamples / this.speechLevels.length;
    const musicConsistency = musicSamples / this.musicLevels.length;

    // Enhanced silence detection - multiple criteria
    const SILENCE_CRITERIA = {
      maxPeakLevel: 0.8, // Very low peak
      maxAverageLevel: 0.2, // Very low average
      maxActiveRatio: 0.01, // Almost no activity
      minSilenceLevel: 8.0, // High silence indicator
      maxSpeechLevel: 0.5, // Virtually no speech
    };

    const isSilence =
      peakLevel <= SILENCE_CRITERIA.maxPeakLevel &&
      averageLevel <= SILENCE_CRITERIA.maxAverageLevel &&
      activeRatio <= SILENCE_CRITERIA.maxActiveRatio &&
      averageSilenceLevel >= SILENCE_CRITERIA.minSilenceLevel &&
      averageSpeechLevel <= SILENCE_CRITERIA.maxSpeechLevel;

    // Music/noise detection based on actual data patterns
    const isMusicOnly =
      // Background noise only: Very low levels across the board
      (averageLevel < 3.0 && peakLevel < 10.0 && averageSpeechLevel < 2.0) ||
      // Music without speech: Medium levels, low speech ratio, perfect consistency
      (averageLevel > 15.0 &&
        averageLevel < 40.0 &&
        averageSpeechLevel < 15.0 &&
        speechConsistency === 1.0) ||
      // Music with vocals but no real speech: High consistency but low music level
      (averageLevel > 40.0 &&
        averageMusicLevel < 5.0 &&
        speechConsistency === 1.0) ||
      // Audio with very low speech levels (background noise/music)
      (averageLevel > 2.0 &&
        averageSpeechLevel < 3.0 &&
        speechToMusicRatio < 1.0);

    // Speech detection criteria - adjusted for normal conversational speech
    const SPEECH_CRITERIA = {
      minSpeechLevel: 5.0, // Lowered for normal conversational speech
      minPeakLevel: 20.0, // Lowered to accommodate quieter speakers
      minActiveRatio: 0.5, // Lowered to allow natural pauses in speech
      minSpeechSamples: 8, // Sample requirement for shorter speech
      minSpeechConsistency: 0.4, // Lowered to accommodate natural speech variations
      minSpeechToMusicRatio: 1.5, // Lowered for noisier environments
    };

    const strongSpeechSamples = this.speechLevels.filter(
      (level) => level > SPEECH_CRITERIA.minSpeechLevel,
    ).length;

    // Multiple speech detection criteria - ALL must be met for reliable detection
    const hasStrongSpeech =
      averageSpeechLevel >= SPEECH_CRITERIA.minSpeechLevel;
    const hasConsistentSpeech =
      strongSpeechSamples >= SPEECH_CRITERIA.minSpeechSamples;
    const hasSpeechConsistency =
      speechConsistency >= SPEECH_CRITERIA.minSpeechConsistency;
    const hasGoodSpeechRatio =
      speechToMusicRatio >= SPEECH_CRITERIA.minSpeechToMusicRatio;
    const hasMinimumActivity =
      peakLevel >= SPEECH_CRITERIA.minPeakLevel &&
      activeRatio >= SPEECH_CRITERIA.minActiveRatio;

    // STRICT: Require ALL criteria to be met for speech detection
    const hasSpeech =
      hasStrongSpeech &&
      hasConsistentSpeech &&
      hasSpeechConsistency &&
      hasGoodSpeechRatio &&
      hasMinimumActivity;

    return {
      totalSamples: this.audioLevels.length,
      averageLevel,
      peakLevel,
      averageSpeechLevel,
      averageMusicLevel,
      speechToMusicRatio,
      activeRatio,
      isSilence,
      isMusicOnly,
      hasSpeech,
      speechConsistency,
      musicConsistency,
    };
  }
}
